import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { Button, Tooltip } from "nlib/ui";
import {
  checkSelectedBusinessHasBusinessUsers,
  checkSelectedBusinessRpaMode,
  getCurrentQuickBooksRealmId,
  getCurrentXeroOrganizationId,
  getSelectedBusinessBookCloseDate,
  getSelectedBusinessData,
  getSelectedBusinessIntegrationServiceData
} from "selectors/businesses";
import { getActiveOrganization } from "selectors/organizations";
import { getLockedTransactions } from "selectors/transactions";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import AuditActions from "actions/AuditActions";
import AuditSubsectionRoutes from "const/AuditSubsectionRoutes";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import GptFineTuneStatuses from "const/GptFineTuneStatuses";
import MatchDocument from "./MatchDocument";
import Objects from "utils/Objects";
import React, { useCallback, useState } from "react";
import Texts from "utils/Texts";
import TransactionFieldsToEdit from "const/TransactionFieldsToEdit";
import Transactions from "utils/Transactions";
import TransactionsActions from "actions/TransactionsActions";
import classNames from "classnames";
import moment from "moment";
import useShowCommonModal from "hooks/useShowCommonModal";
import useTransactionUtils from "hooks/useTransactionUtils";

const Actions = (props) => {
  const {
    disabled,
    auditRoute,
    selected,
    selectable,
    readyToProcess,
    readyToApprove,
    transaction,
    transaction: {
      id: transactionId,
      status,
      timestamp,
      aiProcessing,
      attachmentId,
      extraData = {}
    },
    transactionState,
    transactionState: { manualMode, reason },
    refetchTableData,
    onSelectedChange
  } = props;

  const dispatch = useDispatch();

  const showCommonModal = useShowCommonModal();

  const selectedBusinessIntegrationServiceData = useSelector(getSelectedBusinessIntegrationServiceData);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const closedBookDate = selectedBusinessBookCloseDate
    && moment(timestamp).isSameOrBefore(moment(selectedBusinessBookCloseDate));

  const { uiTexts, messages } = useSelector(getTextsData);

  const {
    extraData: { integrationServiceConnected, lastGptAiFineTuneStatus } = {},
    settings: { advancedDocumentsWorkflow } = {}
  } = useSelector(getSelectedBusinessData);

  const rpaMode = useSelector(checkSelectedBusinessRpaMode);

  const { businessOrganization } = useSelector(getActiveOrganization);

  const selectedBusinessHasBusinessUsers = useSelector(checkSelectedBusinessHasBusinessUsers);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const lockedTransactions = useSelector(getLockedTransactions);

  const locked = !!lockedTransactions[transactionId];

  const { onTransactionApplyChanges } = useTransactionUtils();

  const needReactionStatus = status === DataStatuses.NEED_REACTION;

  const exportedStatus = status === DataStatuses.EXPORTED;

  const allowAskClient = selectable && (!businessOrganization || selectedBusinessHasBusinessUsers);

  const allCategorizedRoute = auditRoute === AuditSubsectionRoutes.ALL_CATEGORIZED;

  const hasChanges = Transactions.hasChanges(transactionState, transaction);

  const contactChangeValid = xeroBusiness || Transactions.validateContactChange(transactionState, transaction);

  const [tooltipOpened, setTooltipOpened] = useState(false);

  const markAsRevised = useCallback(async() => {
    if (!contactChangeValid) return false;

    const fieldsToEdit = Object.values(TransactionFieldsToEdit).filter((fieldName) => {
      return fieldName !== TransactionFieldsToEdit.REASON;
    });

    const dataToEdit = Objects.getProps(transactionState, fieldsToEdit);

    const transactionHasChange = !Objects.checkDeepEqualityByProps(dataToEdit, Objects.getProps(transaction, fieldsToEdit));

    if (transactionHasChange && closedBookDate) {
      const closedBookTimeText = moment.utc(selectedBusinessBookCloseDate).format(DateTimeFormats.DATE_TEXT_EXT);

      showCommonModal({
        text: Texts.replaceVars(messages.closedBookWarning, {
          date: closedBookTimeText,
          service: selectedBusinessIntegrationServiceData.label
        }),
        headerText: uiTexts.warning
      });

      return false;
    }

    let result = !xeroBusiness && transactionHasChange
      ? await dispatch(TransactionsActions.editTransaction(transactionId, dataToEdit, false, true))
      : true;

    if (result) {
      result = await dispatch(AuditActions.markAsRevised({ transactionsIds: [transactionId], currentRoute: auditRoute }));
    }

    return result;
  }, [
    transactionState,
    closedBookDate,
    transaction,
    xeroBusiness,
    dispatch,
    transactionId,
    selectedBusinessBookCloseDate,
    showCommonModal,
    messages.closedBookWarning,
    selectedBusinessIntegrationServiceData.label,
    uiTexts.warning,
    auditRoute,
    contactChangeValid
  ]);

  const handleSaveButtonClick = useCallback(async() => {
    const result = await onTransactionApplyChanges(transaction, transactionState);

    if (result) refetchTableData();
  }, [transaction, transactionState, refetchTableData, onTransactionApplyChanges]);

  const handleAskClientClick = useCallback(async() => {
    if (needReactionStatus && !rpaMode) {
      await dispatch(TransactionsActions.bulkTransactionsUpdate({
        ids: [transactionId],
        data: { status: DataStatuses.TO_RECONCILE }
      }));
      refetchTableData();
    } else {
      onSelectedChange(transactionId, !selected);
    }
  }, [rpaMode, needReactionStatus, dispatch, transactionId, onSelectedChange, selected, refetchTableData]);

  const handleMarkAsRevisedClick = useCallback(async() => {
    const result = await markAsRevised();

    if (result) refetchTableData();
  }, [markAsRevised, refetchTableData]);

  const handleGoToClick = useCallback(async() => {
    const { xeroSourceUrl, quickBooksSourceUrl } = extraData;

    window.open(xeroBusiness ? xeroSourceUrl : quickBooksSourceUrl);
    if (xeroBusiness) {
      const result = await markAsRevised();

      if (result) refetchTableData();
    }
  }, [extraData, xeroBusiness, markAsRevised, refetchTableData]);

  const handleUncategorizedGoToClick = useCallback(() => {
    const { xeroSourceUrl, quickBooksSourceUrl } = extraData;

    window.open(xeroBusiness ? xeroSourceUrl : quickBooksSourceUrl);
  }, [extraData, xeroBusiness]);

  const handleMouseEnter = useCallback(() => {
    setTooltipOpened(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipOpened(false);
  }, []);

  if (auditRoute) {
    const disabledApply = (allCategorizedRoute && !hasChanges) || locked || !contactChangeValid;

    const dangerState = hasChanges && closedBookDate;

    return (
      <div className={Css.actions}>
        {(xeroBusiness || auditRoute === AuditSubsectionRoutes.DUPLICATES) && (
          <div
            className={Css.buttonWrap}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <Button
              outline secondary
              icon={Icons.ArrowSquareOut}
              onClick={handleGoToClick}>
              {uiTexts.open}
            </Button>
            <Tooltip
              opened={tooltipOpened}
              place="left"
              contentClassName={Css.tooltip}>
              <Icons.Warning />
              <span>{xeroBusiness ? messages.auditTransactionsXeroRedirect : messages.auditDuplicateQboTransaction}</span>
            </Tooltip>
          </div>
        )}
        {(!allCategorizedRoute || !xeroBusiness) && (
          <Button
            outline
            className={classNames(Css.applyButton)}
            success={!dangerState && (!allCategorizedRoute || hasChanges)}
            danger={dangerState}
            disabled={disabledApply}
            icon={dangerState ? Icons.Warning : (allCategorizedRoute ? Icons.Check : Icons.ThumbsUp)}
            onClick={handleMarkAsRevisedClick}>
            {(allCategorizedRoute || hasChanges) ? uiTexts.applyChanges : uiTexts.markAsCorrect}
          </Button>
        )}
      </div>
    );
  }
  if (rpaMode && !exportedStatus) {
    const rpaDisableApprove = ((manualMode || !needReactionStatus) ? !readyToApprove : !readyToProcess);

    return (
      <div className={classNames(Css.actions, quickBooksBusiness && Css.quickBooksBusiness)}>
        <MatchDocument
          className={Css.buttonWrap}
          attachmentId={attachmentId}
          disabled={disabled || aiProcessing}
          transaction={transaction}
          refetchTableData={refetchTableData} />
        {allowAskClient && (
          <Button
            outline
            data-id={transactionId}
            warning={!selected}
            danger={selected}
            icon={selected ? Icons.X : Icons.Question}
            onClick={handleAskClientClick}>
            {selected ? uiTexts.doNotAsk : uiTexts.askClient}
          </Button>
        )}
        {(() => {
          if (aiProcessing) {
            return (
              <Button
                outline
                disabled
                className={classNames(Css.processing, Css.lastButton)}
                icon={Icons.Spinner}>
                <span>{uiTexts.processing}</span>
              </Button>
            );
          }
          if (needReactionStatus && !manualMode && lastGptAiFineTuneStatus === GptFineTuneStatuses.SUCCEEDED) {
            return (
              <Button
                outline
                disabled={!(reason || "").trim()}
                icon={Icons.Robot}
                className={Css.lastButton}
                onClick={handleSaveButtonClick}>
                <span>{uiTexts.aiCategorize}</span>
              </Button>
            );
          }

          return (
            <Button
              outline
              disabled={rpaDisableApprove}
              primary={!rpaDisableApprove && needReactionStatus}
              success={!rpaDisableApprove && (!needReactionStatus || manualMode)}
              danger={!rpaDisableApprove && closedBookDate}
              icon={!rpaDisableApprove && closedBookDate ? Icons.Warning : Icons.Check}
              className={Css.lastButton}
              onClick={handleSaveButtonClick}>
              <span>{uiTexts.approve}</span>
            </Button>
          );
        })()}
      </div>
    );
  }

  const disableApprove = status === DataStatuses.EXPORTED
    ? (!quickBooksBusiness || !integrationServiceConnected || !hasChanges || !contactChangeValid)
    : !readyToApprove;

  return (
    <div className={classNames(Css.actions, quickBooksBusiness && Css.quickBooksBusiness)}>
      {advancedDocumentsWorkflow && (
        <MatchDocument
          className={Css.buttonWrap}
          attachmentId={attachmentId}
          disabled={disabled || aiProcessing}
          transaction={transaction}
          refetchTableData={refetchTableData} />
      )}
      {allowAskClient && (
        <Button
          outline
          data-id={transactionId}
          warning={!selected && !needReactionStatus}
          danger={selected || needReactionStatus}
          icon={(selected || needReactionStatus) ? Icons.X : Icons.Question}
          onClick={handleAskClientClick}>
          {(selected || needReactionStatus) ? uiTexts.doNotAsk : uiTexts.askClient}
        </Button>
      )}
      {xeroBusiness && [DataStatuses.EXPORTED, DataStatuses.TO_REPORT].includes(status)
        ? (
          <div
            className={Css.buttonWrap}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <Button
              outline secondary
              icon={Icons.ArrowSquareOut}
              onClick={handleUncategorizedGoToClick}>
              {uiTexts.open}
            </Button>
            <Tooltip
              opened={tooltipOpened}
              place="left"
              contentClassName={Css.tooltip}>
              <Icons.Warning />
              <span>{messages.transactionsXeroRedirect}</span>
            </Tooltip>
          </div>
        )
        : (
          <Button
            outline
            disabled={disableApprove}
            className={Css.lastButton}
            title={exportedStatus ? uiTexts.applyChanges : uiTexts.approve}
            icon={!disableApprove && closedBookDate ? Icons.Warning : Icons.PaperPlaneTilt}
            success={!closedBookDate && !disableApprove}
            danger={!disableApprove && closedBookDate}
            onClick={handleSaveButtonClick}>
            <span>{exportedStatus ? uiTexts.applyChanges : uiTexts.approve}</span>
          </Button>
        )}
    </div>
  );
};

export default React.memo(Actions);
